import type { ComponentType } from 'react';
import {
  IconAlertTriangle,
  IconBaseline,
  IconBuildingFactory2,
  IconCalendar,
  IconChartBar,
  IconChartLine,
  IconGitBranch,
  IconMapPin,
  IconMapSearch,
  IconSubtask,
  IconUpload,
} from '@tabler/icons-react';
import { getDprIssueTrackerUrl } from '../utils/config';
import {
  APP_ROUTES,
  type DashboardViewId,
  type ReportsViewId,
  getDashboardViewFromPath,
  isDashboardHubActive,
  isDashboardSubnavActive,
  isReportsHubActive,
  isReportsSubnavActive,
  isScheduleHubActive,
  normalizePathname,
  REPORTS_HUB_PATHS,
  SCHEDULE_HUB_PATHS,
} from './appRoutes';

export type DashboardSubnavItem = {
  id: DashboardViewId;
  label: string;
  path: string;
  Icon: ComponentType<{ size?: number | string; stroke?: number | string }>;
};

export type ReportsSubnavItem = {
  id: ReportsViewId;
  label: string;
  path: string;
  Icon: ComponentType<{ size?: number | string; stroke?: number | string }>;
};

export type ScheduleSubnavItem = {
  id: string;
  label: string;
  Icon: ComponentType<{ size?: number | string; stroke?: number | string }>;
  /** In-app route. Omit for external portal links. */
  path?: string;
  /** Full URL for a portal page opened in a new tab. */
  href?: string;
  /**
   * Portal path (e.g. `requests/dprIssueTrackerProcess`) opened via openPortalUrl
   * so shared cookies + projectId are applied.
   */
  portalPath?: string;
};

export const DASHBOARD_SUBNAV_ITEMS: DashboardSubnavItem[] = [
  { id: 'overview', label: 'Dashboard', path: APP_ROUTES.dashboard.overview, Icon: IconChartBar },
];

export const REPORTS_SUBNAV_ITEMS: ReportsSubnavItem[] = [
  {
    id: 'location-health',
    label: 'Location health',
    path: APP_ROUTES.reports.locationHealth,
    Icon: IconMapSearch,
  },
  {
    id: 'dependency-blockers',
    label: 'Dependency blockers',
    path: APP_ROUTES.reports.dependencyBlockers,
    Icon: IconGitBranch,
  },
  {
    id: 's-curve',
    label: 'S-curve',
    path: APP_ROUTES.reports.sCurve,
    Icon: IconChartLine,
  },
];

export const SCHEDULE_SUBNAV_ITEMS: ScheduleSubnavItem[] = [
  { id: 'import', path: APP_ROUTES.schedule.import, label: 'Project setup', Icon: IconUpload },
  { id: 'locations', path: APP_ROUTES.schedule.locations, label: 'Location management', Icon: IconMapPin },
  { id: 'tasks', path: APP_ROUTES.schedule.tasks, label: 'Tasks - Gantt', Icon: IconSubtask },
  { id: 'baselines', path: APP_ROUTES.schedule.baselines, label: 'Baselines', Icon: IconBaseline },
  { id: 'calendar', path: APP_ROUTES.schedule.calendar, label: 'Calendar', Icon: IconCalendar },
  {
    id: 'resources',
    path: APP_ROUTES.schedule.resources,
    label: 'Resource management',
    Icon: IconBuildingFactory2,
  },
  {
    id: 'issues',
    label: 'Issues / Hindrances',
    portalPath: 'requests/dprIssueTrackerProcess',
    href: getDprIssueTrackerUrl(),
    Icon: IconAlertTriangle,
  },
];

/** @deprecated Use SCHEDULE_SUBNAV_ITEMS */
export const MANAGE_SCHEDULE_SUBNAV_ITEMS = SCHEDULE_SUBNAV_ITEMS;

export {
  APP_ROUTES,
  getDashboardViewFromPath,
  isDashboardHubActive,
  isDashboardSubnavActive,
  isReportsHubActive,
  isReportsSubnavActive,
  isScheduleHubActive,
  normalizePathname,
  REPORTS_HUB_PATHS,
  SCHEDULE_HUB_PATHS,
};

export function getDashboardView(pathname: string): DashboardViewId {
  return getDashboardViewFromPath(pathname);
}
